import {
    Button,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Tooltip
} from "@mui/material";
import { VEHICLE_TYPE } from "../../../constants/enum";
import { getVehicleTypeLabel } from "../../../utils/vehicle";
import { useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { useState } from "react";
import SearchBar from "../../../components/SearchBar";
import CommonBreadcrumb from "../../../components/CommonBreadcrumb";
import SwitchToggle from "../../../components/SwitchToggle";
import AddVariant from "../../../Modals/addVariant";
import React from "react";
import {
    useGetBrandsQuery,
    useGetCategoriesQuery,
    useGetModelsQuery,
    useGetVariantsQuery,
    useDeleteVariantMutation,
    useUpdateVariantMutation
} from "../../../service/features/api";
import Pagination from "../../../components/Panigation";
import { usePermission } from "../../../hooks/usePermission";
import Can from "../../../components/Can";
import moment from "moment";
import DeleteConfirmModal from "../../../Modals/DeleteConfirmModal";
import { toast } from "react-toastify";

const ManageVariant = () => {
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const canEdit = hasPermission("Vehicle Specification", "isEdit");
    const [searchTerm, setSearchTerm] = useState("");
    const [category, setCategory] = useState('');
    const [brand, setBrand] = useState('');
    const [model, setModel] = useState('');
    const [vehicleType, setVehicleType] = useState<string>('');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [openModal, setOpenModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState<any>(null);

    // Queries
    const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100, brandId: brand });
    const { data: brandsData } = useGetBrandsQuery({ page: 1, limit: 100 });

    // Fetch models only if brand is selected, or fetch all? 
    // Usually dependent. If no brand, maybe show empty or all?
    // Let's fetch models based on brand if selected.
    const { data: modelsData } = useGetModelsQuery(
        { page: 1, limit: 100, brandId: brand || undefined },
        { skip: !brand }
    );

    const { data: variantsData, isLoading, isFetching } = useGetVariantsQuery({
        page,
        limit,
        search: searchTerm,
        categoryId: category || undefined,
        brandId: brand || undefined,
        modelId: model || undefined,
        type: vehicleType || undefined
    });

    const [deleteVariant, { isLoading: isDeleting }] = useDeleteVariantMutation();
    const [updateVariant] = useUpdateVariantMutation();

    const handleCategoryChange = (event: SelectChangeEvent) => {
        setCategory(event.target.value as string);
        setPage(1);
    };

    const handleBrandChange = (event: SelectChangeEvent) => {
        setBrand(event.target.value as string);
        setModel(''); // Reset model when brand changes
        setCategory(''); // Reset category when brand changes
        setPage(1);
    };

    const handleModelChange = (event: SelectChangeEvent) => {
        setModel(event.target.value as string);
        setPage(1);
    };

    const handleVehicleTypeChange = (event: SelectChangeEvent) => {
        setVehicleType(event.target.value as string);
        setPage(1);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedVariant(null);
    };

    const handleEdit = (variant: any) => {
        setSelectedVariant(variant);
        setOpenModal(true);
    };

    const handleDeleteClick = (variant: any) => {
        setSelectedVariant(variant);
        setOpenDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedVariant?._id) return;
        try {
            await deleteVariant(selectedVariant._id).unwrap();
            toast.success("Variant deleted successfully / تم حذف المتغير بنجاح");
            setOpenDeleteModal(false);
            setSelectedVariant(null);
            if (variants.length === 1 && page > 1) {
                setPage(page - 1);
            }
        } catch (err: any) {
            toast.error(err?.data?.message || "Failed to delete variant / فشل حذف المتغير");
        }
    };

    const handleToggle = async (variant: any, field: string, value: boolean) => {
        const payload = {
            id: variant._id,
            name: variant.name,
            categoryId: variant.categoryId || (variant.category?.map((c: any) => c._id) || []),
            brandId: variant.brandId || variant.brand?._id,
            modelId: variant.modelId || variant.model?._id,
            images: variant.images?.length ? variant.images : (variant.image ? [variant.image] : []),
            isPopular: variant.isPopular,
            isActive: variant.isActive,
            type: variant.type || variant.vehicleType || 1,
            order: variant.order || 1,
            [field]: value
        };

        try {
            await updateVariant(payload).unwrap();
            toast.success("Updated successfully / تم التحديث بنجاح");
        } catch (err: any) {
            toast.error(err?.data?.message || "Update failed / فشل التحديث");
        }
    };

    const variants = variantsData?.data?.data || [];
    const paginator = variantsData?.data?.paginator || {};

    return (
        <>
            <CommonBreadcrumb
        title="Variant Management / إدارة المتغيرات"
        breadcrumbItems={[
          { label: "Dashboard / لوحة المعلومات", onClick: () => navigate("/dashboard") },
          { label: "Listed Vehicles / المركبات المدرجة", onClick: () => navigate("/manage-vehicles") },
          { label: "Variant Management / إدارة المتغيرات" },
        ]}
      />

            <div className="cards">
                <div className="table_header">
                    <div className="left_s">
                        <SearchBar
                            searchTerm={searchTerm}
                            setDebouncedSearchTerm={(term) => {
                                setSearchTerm(term);
                                setPage(1);
                            }}
                            placeholder="Search Variants... / ابحث عن المتغيرات..."
                        />
                        <div className="form">
                            <div className="control_group">
                                <Select
                                    fullWidth
                                    value={category}
                                    onChange={handleCategoryChange}
                                    displayEmpty
                                >
                                    <MenuItem value="">Select Category / اختر الفئة</MenuItem>
                                    {categoriesData?.data?.data?.map((cat: any) => (
                                        <MenuItem key={cat._id} value={cat._id}>
                                            {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div className="control_group">
                                <Select
                                    fullWidth
                                    value={brand}
                                    onChange={handleBrandChange}
                                    displayEmpty
                                >
                                    <MenuItem value="">Select Brand / اختر العلامة التجارية</MenuItem>
                                    {brandsData?.data?.data?.map((br: any) => (
                                        <MenuItem key={br._id} value={br._id}>
                                            {br.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div className="control_group">
                                <Select
                                    fullWidth
                                    value={model}
                                    onChange={handleModelChange}
                                    displayEmpty
                                    disabled={!brand}
                                >
                                    <MenuItem value="">Select Model / اختر الطراز</MenuItem>
                                    {modelsData?.data?.data?.map((md: any) => (
                                        <MenuItem key={md._id} value={md._id}>
                                            {md.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div className="control_group">
                                <Select
                                    fullWidth
                                    value={vehicleType}
                                    onChange={handleVehicleTypeChange}
                                    displayEmpty
                                >
                                    <MenuItem value="">Select Vehicle Type / اختر نوع المركبة</MenuItem>
                                    {Object.values(VEHICLE_TYPE)
                                        .filter((v) => typeof v === "number")
                                        .map((type) => (
                                            <MenuItem key={type} value={type.toString()}>
                                                {getVehicleTypeLabel(type as number)}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </div>
                        </div>
                    </div>
                    <div className="right_s">
                        <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
                            <Button
                                className="btn btn_primary"
                                onClick={() => {
                                    setSelectedVariant(null);
                                    setOpenModal(true);
                                }}
                            >
                                <AddIcon /> Add Variant / إضافة متغير
                            </Button>
                        </Can>
                    </div>
                </div>

                <TableContainer className="table_container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr. No. / الرقم التسلسلي</TableCell>
                                <TableCell>Variant Detail / تفاصيل المتغير</TableCell>
                                <TableCell>Vehicle Type / نوع المركبة</TableCell>
                                <TableCell>Category / الفئة</TableCell>
                                <TableCell>Brand / العلامة التجارية</TableCell>
                                <TableCell>Model / الطراز</TableCell>
                                <TableCell>Created Date / تاريخ الإنشاء</TableCell>
                                <TableCell>Disable/Enable / تعطيل/تفعيل</TableCell>
                                <TableCell>Popular / شائع</TableCell>
                                <TableCell>Actions / الإجراءات</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading || isFetching ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" style={{ padding: '2rem' }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : variants.length > 0 ? (
                                variants.map((row: any, index: number) => (
                                    <TableRow hover key={row._id}>
                                        <TableCell>{(paginator.currentPage - 1) * paginator.perPage + index + 1}</TableCell>
                                        <TableCell>
                                            <div className="user_block">
                                                <figure className="no_radius">
                                                    <img
                                                        src={(row.images && row.images.length > 0) ? row.images[0] : (row.image || "/static/images/placeholder.png")}
                                                        alt={row.name}
                                                        onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }}
                                                    />
                                                </figure>
                                                <p>
                                                    <b>{row.name || "--"}</b>
                                                </p>
                                            </div>
                                        </TableCell>
                                        <TableCell>{getVehicleTypeLabel(row.type)}</TableCell>
                                        <TableCell>
                                            {(row.category || row.categories || row.categoryId)?.map((c: any) => c.name).join(", ") || (Array.isArray(row.categoryName) ? row.categoryName.join(", ") : row.categoryName) || "--"}
                                        </TableCell>
                                        <TableCell>{row.brand?.name || "--"}</TableCell>
                                        <TableCell>{row.model?.name || "--"}</TableCell>
                                        <TableCell>{moment(row.createdAt).format('DD-MMM-YYYY')}</TableCell>
                                        <TableCell>
                                            {canEdit ? (
                                                <SwitchToggle
                                                    checked={row.isActive}
                                                    onChange={(e) => handleToggle(row, "isActive", e.target.checked)}
                                                />
                                            ) : (
                                                <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                                                    {row.isActive ? "Enabled / مفعل" : "Disabled / معطل"}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {canEdit ? (
                                                <SwitchToggle
                                                    checked={row.isPopular}
                                                    onChange={(e) => handleToggle(row, "isPopular", e.target.checked)}
                                                />
                                            ) : (
                                                <span className={`status_btn ${row.isPopular ? "c_dark_info" : "c_dark_warning"}`}>
                                                    {row.isPopular ? "Yes / نعم" : "No / لا"}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="table_actions v2">
                                                <Tooltip title="View / عرض" arrow>
                                                    <IconButton onClick={() => navigate("/manage-variant/detail", { state: { id: row._id } })}>
                                                        <img src="/static/images/eye_icon.svg" alt="View / عرض" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                                                    <Tooltip title="Edit / تعديل" arrow>
                                                        <IconButton onClick={() => handleEdit(row)}>
                                                            <img src="/static/images/edit2_icon.svg" alt="Edit / تعديل" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Can>
                                                <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                                                    <Tooltip title="Delete / حذف" arrow>
                                                        <IconButton
                                                            onClick={() => handleDeleteClick(row)}
                                                        >
                                                            <img src="/static/images/trash2_icon.svg" alt="Delete / حذف" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Can>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        No variants found / لا توجد متغيرات
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {paginator && paginator.pageCount > 1 && (
                    <Pagination
                        module={variants}
                        page={page}
                        onPageChange={(p) => setPage(p)}
                        totalPages={paginator.pageCount}
                        totalCount={paginator.itemCount}
                        setPage={setPage}
                    />
                )}
            </div>

            <AddVariant
                open={openModal}
                onClose={handleCloseModal}
                setOpen={setOpenModal}
                variantData={selectedVariant}
            />

            <DeleteConfirmModal
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                message={`Are you sure you want to delete the variant "${selectedVariant?.name}"? / هل أنت متأكد أنك تريد حذف المتغير "${selectedVariant?.name}"؟`}
            />
        </>
    );
};

export default ManageVariant;
